import React, { useRef, useState, useEffect } from'react';
import { Pause, Play } from'lucide-react';
import clsx from'clsx';

/**
 * Auto-scrolling real-time table with status indicators.
 *
 * Visual language matches DataTable: no zebra striping,
 * thin row dividers, uppercase tracked header, subtle hover. Error and
 * slow-call rows are tinted to draw attention.
 *
 * Pause behaviour: polling continues upstream, but when paused the table
 * renders a frozen snapshot captured at the moment Pause was pressed. A
 * badge surfaces how many new calls have arrived while paused so the
 * operator knows there is fresh data waiting on resume.
 */
export default function RealTimeTable({ data, columns, maxRows = 100, onRowClick }) {
 const [paused, setPaused] = useState(false);
 const [snapshot, setSnapshot] = useState([]);
 const tableRef = useRef();

 // Capture the frozen snapshot at the instant we pause.
 const handleTogglePause = () => {
 setPaused((prev) => {
 const next = !prev;
 if (next) {
 // Freeze the current live data.
 setSnapshot(data || []);
 }
 return next;
 });
 };

 // While paused, keep the snapshot stable even as `data` refetches.
 // On resume, drop the snapshot so the live feed takes over again.
 useEffect(() => {
 if (!paused) {
 setSnapshot([]);
 }
 }, [paused]);

 // What actually renders: frozen snapshot while paused, else live data.
 const displayData = paused ? snapshot : (data || []);

 // Identify rows that arrived while paused (not present in the snapshot).
 const snapshotIds = new Set(snapshot.map((r, i) => r.OperationId ?? `idx-${i}`));
 const newWhilePaused = paused
 ? (data || []).filter((r, i) => !snapshotIds.has(r.OperationId ?? `idx-${i}`)).length
 : 0;

 return (
 <div className="chart-container">
 <div className="flex items-center justify-between mb-3">
 <div className="flex items-center gap-2">
 <span
 className={clsx(
'status-dot',
 paused ?'status-dot--muted' :'live-indicator status-dot--success'
 )}
 />
 <span className="text-sm font-semibold text-gray-700">
 Live Feed
 </span>
 <span className="text-xs text-gray-400 tabular-nums">
 · {displayData.length} {displayData.length === 1 ?'call' :'calls'}
 </span>
 {paused && (
 <span className="text-xs font-medium text-amber-600">
 {newWhilePaused > 0
 ? `· Paused · ${newWhilePaused} new ${newWhilePaused === 1 ?'call' :'calls'} waiting`
 :'· Paused'}
 </span>
 )}
 </div>
 <button
 onClick={handleTogglePause}
 className={clsx(
'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border transition',
 paused
 ?'border-emerald-400 text-emerald-600 hover:bg-emerald-50'
 :'border-gray-300 text-gray-600 hover:bg-gray-50'
 )}
 >
 {paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
 {paused ?'Resume' :'Pause'}
 </button>
 </div>

 <div
 ref={tableRef}
 className="overflow-auto max-h-[600px] rounded-lg border border-gray-200"
 >
 <table className="w-full text-xs">
 <thead className="bg-gray-50 sticky top-0 z-10 backdrop-blur">
 <tr>
 {columns.map((col) => (
 <th
 key={col.key}
 className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200"
 style={{ width: col.width }}
 >
 {col.label}
 </th>
 ))}
 </tr>
 </thead>            <tbody>
 {displayData.slice(0, maxRows).map((row, idx) => {
 const statusCode = parseInt(row.StatusCode, 10);
 const isError = statusCode >= 400;
 const isSlow = parseFloat(row.DurationMs) > 5000;
 return (
 <tr
 key={row.OperationId || idx}
 className={clsx(
'transition-colors border-b border-gray-100 last:border-0',
'hover:bg-slate-50',
 isError &&'bg-red-50/40',
 isSlow && !isError &&'bg-amber-50/40',
 onRowClick &&'cursor-pointer'
 )}
 onClick={onRowClick ? () => onRowClick(row) : undefined}
 >
 {columns.map((col) => (
 <td
 key={col.key}
 className="px-3 py-2 text-gray-700 whitespace-nowrap"
 >
 {col.render ? col.render(row[col.key], row) : (row[col.key] ??'—')}
 </td>
 ))}
 </tr>
 );
 })}              {(!displayData || displayData.length === 0) && (
 <tr>
 <td
 colSpan={columns.length}
 className="px-3 py-12 text-center text-sm text-gray-400"
 >
 Waiting for live calls…
 </td>
 </tr>
 )}
 </tbody>
 </table>
 </div>
 </div>
 );
}
